import _ from "lodash";
import ZArray from "./ZArray";
import Accessor from "./Accessor";
import ZTime from "./ZTime";
import AlertX from "./AlertX";

/**
 * @typedef {import("./LangX").lang} lang 
 * @typedef {"required" | "email" | "number"
 *  | "plain" | "plainSpace" | "plainAt" | "plainLower"
 *  | "boolTrue" | "integer" | "isTime" | "isDate"
 *  | "isDateArray" | "isDateRange"
 *  | "isHKID" | "isHKIDFull" | "isHKPhone"
 *  | "atLeastOneLowerCase" | "atLeastOneUpperCase"
 *  | "atLeastOneNumeric" | "atLeastOneSpecialChar"
 *  | "noChinese" | "isString"} fvalidate
 */

class ZValidate {

  static Rules = {
    required: value => (!_.isUndefined(value) && !_.isNull(value)) && (!_.isEmpty(value) || _.isNumber(value) || _.isBoolean(value)),
    email: value => !value || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value),
    number: value => !_.isNaN(Number(value)) || _.isUndefined(value),
    plain: value => !value || /^[a-zA-Z0-9_]+$/i.test(value),
    plainSpace: value => !value || /^[a-zA-Z0-9_ -]+$/i.test(value),
    plainAt: value => !value || /^[a-zA-Z0-9_@]+$/i.test(value),
    plainLower: value => !value || /^[a-z0-9_]+$/.test(value),
    boolTrue: value => value === true,
    integer: value => _.isInteger(Number(value)) || _.isUndefined(value),
    isTime: value => !value || /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value),
    isDate: value => !value || ZTime.Moment(value).toISOString() === value,
    isDateArray: value => !value || (_.isArray(value) && _.every(value, v => ZTime.Moment(v).toISOString() === v)),
    isDateRange: value => !value || (
        (ZTime.Moment(value?.start).toISOString() === value?.start)
        && (ZTime.Moment(value?.end).toISOString() === value?.end)),
    isHKID: value => !value || this.IsHKID(value) || this.IsHKID(value, true),
    isHKIDFull: value => !value || this.IsHKID(value, true),
    atLeastOneLowerCase: value => !value || /(?=.*[a-z])/.test(value),
    atLeastOneUpperCase: value => !value || /(?=.*[A-Z])/.test(value),
    atLeastOneNumeric: value => !value || /(?=.*\d)/.test(value),
    atLeastOneSpecialChar: value => !value || /(?=.*[@.#$!%*?&^])/.test(value),
    isHKPhone: value => !value || /^[0-9]{8}$/.test(value),
    noChinese: value => !value || /^[^\u4e00-\u9fa5]+$/.test(value),
    isString: value => !value || _.isString(value),
  };

  static ErrorMsg = {
    required: {
      EN: "Required",
      TC: "必需填寫"
    },
    email: {
      EN: "Invalid email address",
      TC: "無效的Email格式"
    },
    number: {
      EN: "Must be a number",
      TC: "必需為數字"
    },
    plain: {
      EN: "No spaces or special characters except _",
      TC: "不可包含除 _ 外特殊子元",
    },
    plainAt: {
      EN: "No special characters except @ or _",
      TC: "不可包含除 @ 或 _ 外特殊子元",
    },
    plainSpace: {
      EN: "No special characters except space or _",
      TC: "不可包含除 空格 或 _ 外特殊子元",
    },
    plainLower: {
      EN: "Must be in lowercase, no spaces or special characters except _",
      TC: "只接受小寫字母，不可包含除 _ 外特殊子元",
    },
    boolTrue: {
      EN: "Please check if you want to submit this form",
      TC: "你必須勾選這項才可提交表單",
    },
    integer: {
      EN: "Must be a integer",
      TC: "必需為整數"
    },
    isTime: {
      EN: "Time is not valid",
      TC: "時間格式錯誤"
    },
    isDate: {
      EN: "Date is not valid",
      TC: "日期時間格式錯誤"
    },
    isDateArray: {
      EN: "Date is not valid",
      TC: "日期時間格式錯誤"
    },
    isDateRange: {
      EN: "Date range is not valid",
      TC: "日期時間範圍格式錯誤"
    },
    isHKID: {
      EN: "HKID is invalid",
      TC: "HKID 不正確"
    },
    isHKIDFull: {
      EN: "HKID is invalid",
      TC: "HKID 不正確"
    },
    atLeastOneLowerCase: {
      EN: "At least one lowercase alphabet i.e. [a-z]",
      TC: "至少一個小寫字母，即 [a-z]"
    },
    atLeastOneUpperCase: {
      EN: "At least one uppercase alphabet i.e. [A-Z]",
      TC: "至少一個大寫字母，即 [A-Z]"
    },
    atLeastOneNumeric: {
      EN: "At least one Numeric digit i.e. [0-9]",
      TC: "至少一位數字，即 [0-9]"
    },
    atLeastOneSpecialChar: {
      EN: "At least one special character i.e. [‘@’, ‘$’, ‘.’, ‘#’, ‘!’, ‘%’, ‘*’, ‘?’, ‘&’, ‘^’]",
      TC: "至少有一個特殊字符，即 [‘@’, ‘$’, ‘.’, ‘#’, ‘!’, ‘%’, ‘*’, ‘?’, ‘&’, ‘^’]"
    },
    isHKPhone: {
      EN: "Phone number does not conform to Hong Kong format",
      TC: "電話號碼不符合香港格式"
    },
    noChinese: {
      EN: "No Chinese characters",
      TC: "不可包含中文"
    },
    isString: {
      EN: "Must be a string",
      TC: "必需為字串"
    },
    Unknown: {
      EN: "Unknown Error",
      TC: "未知錯誤"
    }
  };

  /**
   * 
   * @param {any} value 
   * @param {fvalidate | fvalidate[]} validators 
   */
  static Check(value, validators){
    validators = ZArray.Ensure(validators);
    return _.every(validators, (o, i) => {
      if(!this.Rules[o]) return false;
      return this.Rules[o]?.(value);
    });
  }

  /**
   * 
   * @param {any} value 
   * @param {fvalidate | fvalidate[]} validators 
   * @param {*} fieldName 
   * @returns 
   */
  static GetErrorMsg(value, validators, fieldName = undefined){
    validators = ZArray.Ensure(validators);
    for(const o of validators){
      if(!this.Rules[o]) return this.ErrorMsg["Unknown"];
      if(!this.Rules[o]?.(value)) {
        if(_.isEmpty(fieldName)){
          return {
            EN: this.ErrorMsg[o].EN,
            TC: this.ErrorMsg[o].TC
          };
        }
        return {
          EN: `${fieldName?.EN ?? fieldName}: ${this.ErrorMsg[o].EN}`,
          TC: `${fieldName?.TC ?? fieldName}: ${this.ErrorMsg[o].TC}`
        };
      }
    }
    return null;
  }

  /**
   * 
   * @param {any} value 
   * @param {fvalidate | fvalidate[]} validators 
   * @param {*} fieldName 
   * @returns 
   */
  static GetAllErrorMessages(value, validators, fieldName = undefined){
    validators = ZArray.Ensure(validators);
    let errors = [];
    for(const o of validators){
      if(!this.Rules[o]) return this.ErrorMsg["Unknown"];
      if(!this.Rules[o]?.(value)) {
        if(_.isEmpty(fieldName)){
          errors.push(this.ErrorMsg[o]);
        }else{
          errors.push({
            EN: `${fieldName?.EN ?? fieldName}: ${this.ErrorMsg[o].EN}`,
            TC: `${fieldName?.TC ?? fieldName}: ${this.ErrorMsg[o].TC}`
          });
        }
      }
    }
    return errors;
  }

  /**
   * 
   * @param {String} value 
   * @param {Number} min 
   * @param {Number} max 
   */
  static CheckLength(formValue, fields, min = 0, max = Infinity){
    if(_.isEmpty(formValue)) return false;
    fields = ZArray.Ensure(fields);
    return _.every(fields, v => {
      let value = Accessor.Get(formValue, v);
      return !value || (value.length >= min && value.length <= max);
    });
  }

  /**
   * 
   * @param {any[]} values 
   * @param {fvalidate | fvalidate[]} validators 
   */
  static CheckAll(values, validators){
    values = ZArray.Ensure(values);
    validators = ZArray.Ensure(validators);
    return _.every(validators, (o, i) => {
      if(!this.Rules[o]) return false;
      return _.every(values, v => this.Rules[o]?.(v));
    });
  }

  /**
   * 
   * @param {String[]} fields 
   * @param {fvalidate | fvalidate[]} validators 
   */
  static CheckAllFields(formValue, fields, validators){
    if(_.isEmpty(formValue)) return false;
    fields = ZArray.Ensure(fields);
    validators = ZArray.Ensure(validators);
    return _.every(validators, (o, i) => {
      if(!this.Rules[o]) return false;
      return _.every(fields, v => {
        let value = Accessor.Get(formValue, v);
        return this.Rules[o]?.(value);
      });
    });
  }

  /**
   * 
   * @param {String[]} fields 
   * @param {fvalidate | fvalidate[]} validators 
   */
  static GetErrors(formValue, fields, validators){
    if(_.isEmpty(formValue)) return [];
    fields = ZArray.Ensure(fields);
    validators = ZArray.Ensure(validators);
    let rtn = [];
    _.map(fields, (o, i) => {
      let value = Accessor.Get(formValue, o);
      let error = this.GetErrorMsg(value, validators, o);
      if(error){
        rtn.push(error);
      }
    });
    return rtn;
  }

  /**
   * 
   * @param {*} formValue 
   * @param {String[]} fields 
   */
  static CheckUploads(formValue, fields){
    fields = ZArray.Ensure(fields);
    return _.every(fields, (o, i) => {
      let values = Accessor.Get(formValue, o);
      if(!values || values.length === 0) return true; 
      return _.every(values, v => !_.isEmpty(v?.refID) && v?.done === true);
    });
  }

  /**
   * 
   * @param {String} str 
   * @returns 
   */
  static IsHKID(str, withBracket = false){ 
    let strValidChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (str.length < 8)
    { 
      return false;
    }
    str = str.toUpperCase();    
    let hkidPat = withBracket ? /^([A-Z]{1,2})([0-9]{6})\(([A0-9])\)$/ : /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
    let matchArray = str.match(hkidPat);   
    if (matchArray === null)
    {
      return false;
    }
    let charPart = matchArray[1];
    let numPart = matchArray[2];
    let checkDigit = matchArray[3];    
    let checkSum = 0;
    if (charPart.length === 2) {
      checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
      checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
    } else {
      checkSum += 9 * 36;
      checkSum += 8 * (10 + strValidChars.indexOf(charPart));
    }
  
    for (let i = 0, j = 7; i < numPart.length; i++, j--)
    {
      checkSum += j * numPart.charAt(i);
    }
    let remaining = checkSum % 11;
    let verify = remaining === 0 ? 0 : 11 - remaining;
    return verify === Number(checkDigit) || (verify === 10 && checkDigit === "A");
  }

  /**
   * 
   * @param {*} formValue 
   * @param {String | String[]} validateFields 
   * @param {fvalidate | fvalidate[]} validators 
   * @returns 
   */
  static CheckAndAlert(formValue, validateFields, validators){
    let valid = this.CheckAllFields(formValue, validateFields, validators);
    if(!valid){
      let errors = this.GetErrors(formValue, validateFields, validators);
      if(errors.length === 0){
        AlertX.RequiredFields();
      }else{
        AlertX.AllErrors(errors, "error", 10000);
      }
    }
    return valid;
  }

  /**
   * @typedef {Object} validateVList 
   * @property {String} name
   * @property {fvalidate | fvalidate[]} validators
   * 
   * @param {*} formValue 
   * @param {validateVList} vlist 
   * @returns {{
   *  isValid: Boolean,
   *  errors: lang[]
   * }}
   */
  static ValidateVList(formValue, vlist, alert = true){
    let errors = [];
    for(const o of vlist){
      let {name, label, validators} = o;
      if(!name || !validators) continue;
      validators = ZArray.Ensure(validators);
      let value = Accessor.Get(formValue, name);
      let error = this.GetAllErrorMessages(value, validators, label);
      errors.push(...error);
    }
    if(errors.length > 0 && alert){
      AlertX.AllErrors(errors, "error", 10000);
    }
    return {isValid: errors.length === 0, errors: errors};
  }

} 

 export default ZValidate;